import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /** @type {Map<HTMLElement, ReturnType<typeof setTimeout>>} */
  #visibilityTimers = new Map();
  #barHeaderObserver;
  #barHidden = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {number|undefined} */
  #resumeFrame = undefined;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotion.addEventListener('change', this.#handleMotionChange);

    this.current = this.#current;
    this.#observeBarHeader();
    document.addEventListener('shopify:section:load', this.#observeBarHeader);
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.#barHeaderObserver?.disconnect();
    document.removeEventListener('shopify:section:load', this.#observeBarHeader);
    for (const [element, timer] of this.#visibilityTimers) {
      clearTimeout(timer);
      element.style.removeProperty('visibility');
    }
    this.#visibilityTimers.clear();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.suspend);
    this.removeEventListener('focusout', this.#handleFocusOut);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotion.removeEventListener('change', this.#handleMotionChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in milliseconds between slides.
   */
  play(interval = this.autoplayInterval) {
    this.suspend();
    this.paused = false;
    if (!this.autoplay || this.#reducedMotion.matches || document.hidden) return;
    if (this.matches(':hover') || this.matches(':focus-within')) return;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || this.matches(':focus-within') || document.hidden || this.#reducedMotion.matches) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    if (this.#resumeFrame !== undefined) {
      cancelAnimationFrame(this.#resumeFrame);
      this.#resumeFrame = undefined;
    }
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused || this.matches(':hover') || this.matches(':focus-within')) return;

    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      const hidden = index !== relativeIndex;
      const wasVisible = slide.getAttribute('aria-hidden') === 'false';
      slide.inert = hidden;
      slide.setAttribute('aria-hidden', `${hidden}`);
      // Opacity keeps its existing 500ms fade. Visibility changes once after it,
      // rather than becoming a non-composited CSS transition.
      this.#setVisibility(slide, hidden, wasVisible ? 500 : 0);
    });
  }

  /** Keep discrete visibility in step with the existing opacity fade, without layout reads. */
  #setVisibility(element, hidden, duration) {
    clearTimeout(this.#visibilityTimers.get(element));
    this.#visibilityTimers.delete(element);
    if (!hidden || !duration || this.#reducedMotion.matches || document.hidden) {
      element.style.visibility = hidden ? 'hidden' : 'visible';
      return;
    }
    element.style.visibility = 'visible';
    const timer = setTimeout(() => {
      this.#visibilityTimers.delete(element);
      element.style.visibility = 'hidden';
    }, duration);
    this.#visibilityTimers.set(element, timer);
  }

  #observeBarHeader = () => {
    this.#barHeaderObserver?.disconnect();
    const bar = this.closest('.announcement-bar');
    const header = document.querySelector('#header-component');
    if (!bar?.classList.contains('announcement-bar--hide-after-scroll') || !header) return;
    const sync = (animate) => {
      const hidden = ['active', 'idle'].includes(header.dataset.stickyState);
      if (animate && hidden === this.#barHidden) return;
      this.#barHidden = hidden;
      this.#setVisibility(bar, hidden, animate ? 180 : 0);
    };
    sync(false);
    this.#barHeaderObserver = new MutationObserver(() => sync(true));
    this.#barHeaderObserver.observe(header, { attributes: true, attributeFilter: ['data-sticky-state'] });
  };

  /**
   * Resume after focus leaves the component and the browser updates focus state.
   */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#resumeFrame = requestAnimationFrame(() => {
      this.#resumeFrame = undefined;
      this.resume();
    });
  };

  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #handleMotionChange = () => (this.#reducedMotion.matches ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
